<template>
  <div/>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

const store = useStore<RootState>();

defineOptions({
  name: 'dataTranslateLanguagePageItems',
});

// Базовый тип для сообщений с ID перевода элемента языковой страницы
type TranslateLanguagePageItemWithId = {
  idTranslateLanguagePageItem: number;
};

// Типы для WebSocket сообщений компонента translateLanguagePageItems
type TranslateLanguagePageItemsListMessage = WebSocketMessageWithFields<'language-page-item-list-translations', {
  translateLanguagePageItems: unknown[];
}>;
type TranslateLanguagePageItemsNotFoundMessage = WebSocketMessageEmpty<'language-page-item-list-translations-not-found'>;
type TranslateLanguagePageItemCreateMessage = WebSocketMessageWithFields<'translate-language-page-item-create' | 'translate-language-page-item-create-success', TranslateLanguagePageItemWithId & {
  idLanguagePageItem: number;
  idLanguage: number;
  text: string;
}>;
type TranslateLanguagePageItemUpdateMessage = WebSocketMessageWithFields<'translate-language-page-item-update' | 'translate-language-page-item-update-success', TranslateLanguagePageItemWithId & {
  text: string;
}>;
type TranslateLanguagePageItemDeleteMessage = WebSocketMessageWithFields<'translate-language-page-item-delete' | 'translate-language-page-item-delete-success', TranslateLanguagePageItemWithId>;
type TranslateLanguagePageItemLockMessage = WebSocketMessageWithFields<'translate-language-page-item-lock' | 'translate-language-page-item-lock-success', TranslateLanguagePageItemWithId>;
type TranslateLanguagePageItemUnlockMessage = WebSocketMessageWithFields<'translate-language-page-item-unlock' | 'translate-language-page-item-unlock-success', TranslateLanguagePageItemWithId>;
type TranslateLanguagePageItemUpdateGlobalMessage = WebSocketMessageWithFields<'translate-language-page-item-update-global', {
  i18n: {
    pageName: string;
    pageItemCode: string;
    text: string;
  };
}>;

// Объединенный тип для всех сообщений
type TranslateLanguagePageItemsWebSocketMessage = 
  | TranslateLanguagePageItemsListMessage 
  | TranslateLanguagePageItemsNotFoundMessage 
  | TranslateLanguagePageItemCreateMessage 
  | TranslateLanguagePageItemUpdateMessage 
  | TranslateLanguagePageItemDeleteMessage 
  | TranslateLanguagePageItemLockMessage 
  | TranslateLanguagePageItemUnlockMessage 
  | TranslateLanguagePageItemUpdateGlobalMessage;

// WebSocket message handler
const handleWebSocketMessage = (data: TranslateLanguagePageItemsWebSocketMessage): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'language-page-item-list-translations':
      store.dispatch('translateLanguagePageItems/wsSetTranslateLanguagePageItems', {
        translateLanguagePageItems: data.translateLanguagePageItems,
      });
      break;
    case 'language-page-item-list-translations-not-found':
      store.dispatch('languagePageItem/wsSetEmptyLanguagePageItem');
      store.dispatch('languagePage/wsSetEmptyLanguagePage');
      break;

    case 'translate-language-page-item-create':
    case 'translate-language-page-item-create-success':
      store.dispatch('translateLanguagePageItems/wsTranslateLanguagePageItemCreate', {
        idTranslateLanguagePageItem: data.idTranslateLanguagePageItem,
        idLanguagePageItem: data.idLanguagePageItem,
        idLanguage: data.idLanguage,
        text: data.text,
      });
      break;

    case 'translate-language-page-item-update':
    case 'translate-language-page-item-update-success':
      store.dispatch('translateLanguagePageItems/wsTranslateLanguagePageItemUpdate', {
        idTranslateLanguagePageItem: data.idTranslateLanguagePageItem,
        text: data.text,
      });
      break;

    case 'translate-language-page-item-delete':
    case 'translate-language-page-item-delete-success':
      store.dispatch('translateLanguagePageItems/wsTranslateLanguagePageItemDelete', {
        idTranslateLanguagePageItem: data.idTranslateLanguagePageItem,
      });
      break;

    case 'translate-language-page-item-lock':
    case 'translate-language-page-item-lock-success':
      store.dispatch('translateLanguagePageItems/wsTranslateLanguagePageItemLock', {
        idTranslateLanguagePageItem: data.idTranslateLanguagePageItem,
      });
      break;

    case 'translate-language-page-item-unlock':
    case 'translate-language-page-item-unlock-success':
      store.dispatch('translateLanguagePageItems/wsTranslateLanguagePageItemUnlock', {
        idTranslateLanguagePageItem: data.idTranslateLanguagePageItem,
      });
      break;

    case 'translate-language-page-item-update-global':
      store.dispatch('i18n/updatePageItemGlobal', {
        pageName: data.i18n.pageName,
        pageItemCode: data.i18n.pageItemCode,
        text: data.i18n.text,
      });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);
</script>
